import { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

export interface DragHandleProps {
  ref: (node: HTMLElement | null) => void;
  listeners: Record<string, unknown>;
  attributes: Record<string, unknown>;
}

interface SortableItemProps {
  id: string;
  // handleInside: a alça NÃO é renderizada aqui — o item recebe as props e
  // desenha a própria (cabeçalho dentro do card). Sem isso a alça fica ao
  // lado do card e o conjunto parece quebrado.
  handleInside?: boolean;
  children: React.ReactNode | ((handle: DragHandleProps) => React.ReactNode);
}

function SortableItem({ id, handleInside, children }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  if (handleInside) {
    return (
      <div ref={setNodeRef} style={style} className={isDragging ? "shadow-lg rounded-xl" : undefined}>
        {typeof children === "function"
          ? children({ ref: setActivatorNodeRef, listeners: (listeners ?? {}) as Record<string, unknown>, attributes: attributes as unknown as Record<string, unknown> })
          : children}
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} className={`flex items-center gap-2 ${isDragging ? "shadow-lg rounded-lg" : ""}`}>
      <button
        type="button"
        className="touch-none p-1.5 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing shrink-0"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-0">{typeof children === "function" ? children({ ref: setActivatorNodeRef, listeners: (listeners ?? {}) as Record<string, unknown>, attributes: attributes as unknown as Record<string, unknown> }) : children}</div>
    </div>
  );
}

interface SortableListProps<T extends { id: string }> {
  items: T[];
  onReorder: (items: T[]) => void;
  renderItem: (item: T, index: number, handle?: DragHandleProps) => React.ReactNode;
  className?: string;
  // Ver SortableItem: item desenha a própria alça (card inteiro, sem a alça
  // solta do lado).
  handleInside?: boolean;
}

export function SortableList<T extends { id: string }>({
  items,
  onReorder,
  renderItem,
  className,
  handleInside,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorder(newItems);
    }
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item, index) => (
            <SortableItem key={item.id} id={item.id} handleInside={handleInside}>
              {handleInside
                ? (handle: DragHandleProps) => renderItem(item, index, handle)
                : renderItem(item, index)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
